<template>
    <DatePicker
        v-bind="primeVueProps"
        :model-value="normalizedModelValue"
        @update:model-value="$emit('update:modelValue', $event)"
    />
</template>

<script>
import { computed } from 'vue';
/**
 * ETimepicker - Abstraction wrapper around Primevue's Datepicker to replicate Buefy's b-timepicker
 *
 * This component currently wraps Primevue's Datepicker and forwards all props, events, and slots.
 * Timepickers are used for selecting times through an interactive interface.
 *
 * Usage:
 *   <e-timepicker v-model="selectedTime" placeholder="Select time">
 *   </e-timepicker>
 *
 *   <e-timepicker v-model="time" :min-time="minTime" :max-time="maxTime">
 *   </e-timepicker>
 *
 *   <e-timepicker v-model="time" hour-format="12" inline>
 *   </e-timepicker>
 *
 *   <e-timepicker v-model="time" :enable-seconds="true">
 *   </e-timepicker>
 *
 * All Buefy b-timepicker props, events, and slots are supported.
 */
export default {
    name: 'ETimepicker',
    inheritAttrs: false,
    props: {
        modelValue: {
            type: [Date, String],
            default: null,
        },
        placeholder: {
            type: String,
            default: '',
        },
        minTime: {
            type: Date,
            default: null,
        },
        maxTime: {
            type: Date,
            default: null,
        },
        hourFormat: {
            type: String,
            default: '12',
        },
        inline: {
            type: Boolean,
            default: false,
        },
        enableSeconds: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: null,
        },
        icon: {
            type: String,
            default: null,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { attrs }) {
        const normalizedModelValue = computed(() => {
            if (!props.modelValue) return null;
            if (props.modelValue instanceof Date) return props.modelValue;
            if (typeof props.modelValue === 'string') {
                const timeMatch = props.modelValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
                if (timeMatch) {
                    const date = new Date();
                    date.setHours(parseInt(timeMatch[1], 10));
                    date.setMinutes(parseInt(timeMatch[2], 10));
                    date.setSeconds(timeMatch[3] ? parseInt(timeMatch[3], 10) : 0);
                    date.setMilliseconds(0);
                    return date;
                }
                const parsed = new Date(props.modelValue);
                return isNaN(parsed.getTime()) ? null : parsed;
            }
            return null;
        });

        const primeVueProps = computed(() => {
            const pvProps = {
                ...attrs,
                timeOnly: true,
                // Inline time-only panel is gated on currentView === 'date'; keep the date view pinned.
                view: 'date',
                placeholder: props.placeholder,
                minDate: props.minTime,
                maxDate: props.maxTime,
                hourFormat: props.hourFormat === '24' ? '24' : '12',
                inline: props.inline,
                showSeconds: props.enableSeconds,
                disabled: props.disabled,
                fluid: props.expanded,
                showIcon: !!props.icon,
                iconDisplay: props.icon ? 'input' : undefined,
                pt: {
                    dropdown: {
                        class: [
                            'tw:appearance-none',
                            'tw:bg-white',
                            'tw:border tw:border-gray-300',
                            'tw:rounded',
                            'tw:text-gray-700',
                            'tw:cursor-pointer',
                            'tw:block',
                            'tw:text-base',
                            'tw:leading-normal',
                            'tw:py-[calc(0.5em-1px)] tw:pl-3 tw:pr-10',
                            'tw:relative',
                            'tw:align-top',
                            'hover:tw:border-gray-400',
                            'focus:tw:outline-none focus:tw:border-blue-500',
                            'focus:tw:ring focus:tw:ring-blue-200 focus:tw:ring-opacity-50',
                        ].join(' '),
                    },
                },
            };

            if (props.size === 'is-small') {
                pvProps.size = 'small';
            }
            if (props.size === 'is-large') {
                pvProps.size = 'large';
            }

            return pvProps;
        });

        return { normalizedModelValue, primeVueProps };
    },
};
</script>
